import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import ScrollCue from "@/components/ScrollCue";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import TaxServices from "@/pages/TaxServices";
import BusinessServices from "@/pages/BusinessServices";
import HowItWorks from "@/pages/HowItWorks";
import Resources from "@/pages/Resources";
import ArticlePage from "@/pages/ArticlePage";
import Standards from "@/pages/Standards";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import GetStarted from "@/pages/GetStarted";
import ComplexityTool from "@/pages/ComplexityTool";
import Portal from "@/pages/Portal";
import Pricing from "@/pages/Pricing";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import AuthCallback from "@/pages/AuthCallback";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import PayInvoice from "@/pages/PayInvoice";
import BookConsultation from "@/pages/BookConsultation";
import BookingConfirmed from "@/pages/BookingConfirmed";
import BookingPay from "@/pages/BookingPay";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/tax-services" element={<TaxServices />} />
      <Route path="/business-services" element={<BusinessServices />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<ArticlePage />} />
      <Route path="/standards" element={<Standards />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/get-started" element={<GetStarted />} />
      <Route path="/complexity" element={<ComplexityTool />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/pay/:token" element={<PayInvoice />} />
      <Route path="/book" element={<BookConsultation />} />
      <Route path="/book/confirmed" element={<BookingConfirmed />} />
      <Route path="/book/pay/:token" element={<BookingPay />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/security" element={<Legal kind="security" />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App text-steel-text">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Navbar />
          <main>
            <AppRouter />
          </main>
          <Footer />
          <ScrollCue />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}
